export type LigneXlsx = Record<string, unknown>;

export interface FeuilleXlsx {
  nom: string;
  lignes: LigneXlsx[];
}

function nomFeuilleValide(nom: string): string {
  const nettoye = nom.replace(/[\\/?*[\]:]/g, ' ').trim();
  return (nettoye || 'Feuille').slice(0, 31);
}

function valeurLisible(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function valeurCellule(
  value: unknown
): string | number | boolean | Date | null {
  if (value === null || value === undefined) return null;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  ) {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * Construit un fichier XLSX côté navigateur sans interpréter les chaînes comme
 * des formules. La bibliothèque ne crée une formule que lorsque le type
 * `Formula` est explicitement fourni, ce que ce chemin n'utilise jamais.
 */
export async function construireClasseurXlsx(
  feuilles: FeuilleXlsx[]
): Promise<Blob> {
  const { default: writeXlsxFile } = await import('write-excel-file/universal');
  const sheets = feuilles.map(feuille => {
    const colonnes = Array.from(
      new Set(feuille.lignes.flatMap(ligne => Object.keys(ligne)))
    );
    const data = [
      colonnes.map(colonne => ({
        value: colonne,
        type: String,
        fontWeight: 'bold' as const,
      })),
      ...feuille.lignes.map(ligne =>
        colonnes.map(colonne => valeurCellule(ligne[colonne]))
      ),
    ];
    const columns = colonnes.map(cle => {
      const largeurContenu = feuille.lignes.reduce((largeur, ligne) => {
        return Math.max(largeur, valeurLisible(ligne[cle]).length);
      }, cle.length);
      return { width: Math.min(60, Math.max(15, largeurContenu + 2)) };
    });

    return {
      data,
      sheet: nomFeuilleValide(feuille.nom),
      columns,
      stickyRowsCount: 1,
    };
  });

  return writeXlsxFile(sheets).toBlob();
}

export function telechargerClasseurXlsx(blob: Blob, nomFichier: string): void {
  const url = window.URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  lien.style.display = 'none';
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
}
